import { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { LayoutList, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  useCreatePreboardingSection,
  usePreboardingFormSections,
  useReorderPreboardingSections,
} from '@/hooks/use-preboarding-form-builder';
import { SectionCard } from '../builder/section-card';
import { SectionEditorDialog, type SectionDraft } from '../builder/section-editor-dialog';
import { extractApiError } from '../builder/builder-utils';
import type { PreboardingFormStepProps } from './setup-emails-step';

// Step 3 — Form Builder (HW-557). HR breaks the form into sections and fills each
// with employee-field inputs (mapped to the profile via the HW-563 catalog) and
// document-upload blocks. Every change persists immediately to its own table.
export function FormBuilderStep({ form }: PreboardingFormStepProps) {
  const { data: sections, isLoading } = usePreboardingFormSections(form.id);
  const createSection = useCreatePreboardingSection(form.id);
  const reorderSections = useReorderPreboardingSections(form.id);

  const [createOpen, setCreateOpen] = useState(false);

  const sorted = useMemo(
    () =>
      [...(sections ?? [])].sort(
        (a, b) =>
          a.position - b.position ||
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [sections],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sorted.findIndex((s) => s.id === active.id);
      const newIndex = sorted.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(sorted, oldIndex, newIndex);
      reorderSections.mutate(
        { sectionIds: reordered.map((s) => s.id) },
        { onError: (err) => toast.error(extractApiError(err, 'Failed to reorder sections')) },
      );
    },
    [sorted, reorderSections],
  );

  const handleCreate = (draft: SectionDraft) => {
    createSection.mutate(draft, {
      onSuccess: () => setCreateOpen(false),
      onError: (err) => toast.error(extractApiError(err, 'Failed to add section')),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const hasSections = sorted.length > 0;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">Form sections</h2>
          <p className="text-xs text-muted-foreground">
            Build the questionnaire from sections of employee fields and document uploads.
          </p>
        </div>
        {hasSections && (
          <Button size="sm" className="gap-1.5" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add section
          </Button>
        )}
      </div>

      {hasSections ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sorted.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {sorted.map((section) => (
                <SectionCard key={section.id} formId={form.id} section={section} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border py-16 text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <LayoutList className="h-5 w-5" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No sections yet</p>
            <p className="text-xs text-muted-foreground">
              Start by adding a section like “Personal Information” or “Bank Details”.
            </p>
          </div>
          <Button size="sm" className="gap-1.5" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add section
          </Button>
        </div>
      )}

      <SectionEditorDialog
        open={createOpen}
        mode="create"
        isSubmitting={createSection.isPending}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
      />
    </div>
  );
}
